import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiTokenLazyQuery } from "@/generated/graphql";

import { HermesApiError } from "./hermes-fetch";

const TOKEN_STALE_TIME = 82_800_000; // 23 hours

interface TokenInput {
  assessmentId: string;
  workId: string;
}

/**
 * Query Pantheon for the API JWT Token needed to communicate with the Hermes
 * API. Uses `network-only` fetch policy on the Apollo query to avoid returning
 * a cached token that may have expired server-side.
 */
export function useHermesApiToken(input: TokenInput): string | null {
  const [getApiToken] = useApiTokenLazyQuery();
  const { data } = useQuery({
    queryKey: ["apiToken", input.assessmentId, input.workId],
    staleTime: TOKEN_STALE_TIME,
    queryFn: async () => {
      const { data } = await getApiToken({
        variables: input,
        fetchPolicy: "network-only",
      });
      return data?.apiToken ?? null;
    },
  });
  return data ?? null;
}

/**
 * Invalidate the cached Hermes API token so the next request fetches a fresh
 * one. Call this when a Hermes API call returns 401.
 */
export function useInvalidateHermesToken() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["apiToken"] });
}

/**
 * Returns true if the error is a 401 from the Hermes API.
 */
export function isHermesUnauthorized(error: unknown): boolean {
  return error instanceof HermesApiError && error.status === 401;
}
